/**
 * The shared ``<DataTable>`` table-settings cog menu (ADR-038 Phase E;
 * REQ-UI-119 / REQ-UI-121).
 *
 * A Radix Popover anchored to a gear icon, matching the Meridian house
 * menu pattern (:func:`audit/audit-column-settings-menu`,
 * :func:`entity-details/section-visibility-menu`). Contents:
 *
 * 1. **Show columns** — a checkbox per column. The **min-1-column**
 *    invariant is enforced upstream in
 *    :func:`data-table-model.toggleColumnVisibility`; the last visible
 *    column's checkbox renders disabled so the operator can see *why* it
 *    will not toggle rather than watching a click do nothing.
 * 2. **Move left / Move right** — the keyboard-reachable counterpart to
 *    the header drag-reorder (REQ-UI-059: header drag is pointer-only by
 *    nature, so the reorder capability needs a second, operable path).
 * 3. An optional caller-supplied action (Phase I hangs "Export results…"
 *    here, exactly as Flow J's cog does).
 *
 * Cross-references:
 *   - REQ-UI-119 (visible-column selection); REQ-UI-121 (reorder).
 *   - REQ-UI-059 (keyboard-operable menu affordances).
 */
import * as Popover from "@radix-ui/react-popover";
import { ArrowLeft, ArrowRight, CaretDown, Gear } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import type { DataTableColumn } from "@/data-table/data-table-model";
import { cn } from "@/lib/utils";

export interface DataTableColumnMenuProps<TRow> {
  /** The full column set for the selected data type. */
  readonly columns: readonly DataTableColumn<TRow>[];
  /** Effective left-to-right column order — drives the menu's row order. */
  readonly columnOrder: readonly string[];
  /** Currently-visible column keys. */
  readonly visibleColumns: ReadonlySet<string>;
  /** Toggle one column's visibility (caller enforces min-1 + persists). */
  readonly onToggleColumn: (key: string) => void;
  /** Move a column one position toward the given direction. */
  readonly onMoveColumn: (key: string, direction: "left" | "right") => void;
  /** Optional extra menu item rendered below a divider (e.g. Export…). */
  readonly extraAction?: ReactNode;
  /** Test id root. */
  readonly testId?: string;
}

/**
 * Render the table-settings cog.
 *
 * Stateful only for the popover open + the Show-columns section
 * expansion; visibility and order both delegate to the caller, which owns
 * the persisted chrome (D13).
 */
export function DataTableColumnMenu<TRow>({
  columns,
  columnOrder,
  visibleColumns,
  onToggleColumn,
  onMoveColumn,
  extraAction,
  testId = "data-table-settings",
}: DataTableColumnMenuProps<TRow>): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [showColumns, setShowColumns] = useState(true);

  const onlyOneVisible = visibleColumns.size <= 1;
  const byKey = new Map(columns.map((col) => [col.key, col]));
  const ordered = columnOrder
    .map((key) => byKey.get(key))
    .filter((col): col is DataTableColumn<TRow> => col !== undefined);

  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) setShowColumns(true);
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Table settings"
          title="Table settings"
          className="rounded p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
          data-testid={`${testId}-gear`}
        >
          <Gear size={16} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={6}
          className={cn(
            "z-50 max-h-[70vh] min-w-[260px] overflow-auto rounded-md",
            "border border-border bg-bg-overlay shadow-lg",
          )}
          data-testid={`${testId}-menu`}
          aria-label="Table settings"
        >
          <button
            type="button"
            onClick={() => setShowColumns((prev) => !prev)}
            aria-expanded={showColumns}
            aria-controls={`${testId}-columns`}
            data-testid={`${testId}-show-columns`}
            className={cn(
              "flex w-full items-center justify-between gap-2 px-4 py-2.5",
              "text-body-sm text-text-primary hover:bg-bg-elevated",
              "focus:outline-none focus:bg-bg-elevated",
            )}
          >
            <span>Show columns</span>
            <CaretDown
              size={12}
              aria-hidden="true"
              className={cn("transition-transform", showColumns && "rotate-180")}
            />
          </button>

          {showColumns && (
            <div
              id={`${testId}-columns`}
              role="group"
              aria-label="Visible columns"
              data-testid={`${testId}-columns`}
              className="border-t border-border px-2 py-1"
            >
              {ordered.map((col, index) => {
                const checked = visibleColumns.has(col.key);
                const disabled = checked && onlyOneVisible;
                return (
                  <div
                    key={col.key}
                    className="flex items-center gap-1"
                    data-testid={`${testId}-col-${col.key}`}
                  >
                    <label
                      className={cn(
                        "flex flex-1 items-center gap-2 rounded px-2 py-1.5 text-body-sm",
                        disabled
                          ? "cursor-not-allowed text-text-muted"
                          : "cursor-pointer text-text-primary hover:bg-bg-elevated",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => onToggleColumn(col.key)}
                        data-testid={`${testId}-col-checkbox-${col.key}`}
                        className="h-3.5 w-3.5 cursor-pointer accent-accent disabled:cursor-not-allowed"
                      />
                      <span className="truncate">{col.label}</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => onMoveColumn(col.key, "left")}
                      disabled={index === 0}
                      aria-label={`Move ${col.label} left`}
                      title={`Move ${col.label} left`}
                      data-testid={`${testId}-move-left-${col.key}`}
                      className="rounded p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                    >
                      <ArrowLeft size={12} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onMoveColumn(col.key, "right")}
                      disabled={index === ordered.length - 1}
                      aria-label={`Move ${col.label} right`}
                      title={`Move ${col.label} right`}
                      data-testid={`${testId}-move-right-${col.key}`}
                      className="rounded p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                    >
                      <ArrowRight size={12} aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {extraAction !== undefined && (
            <>
              <div className="border-t border-border" />
              {extraAction}
            </>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
